import { Fragment } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  FORECAST_LINE_VISIBILITY,
  colors,
} from 'src/components/rebates/ForecastCardConstants';
import { ForecastReportedLegendEntry } from 'src/components/rebates/ForecastReportedLegendEntry';

export type ForecastLegendEntry = {
  label: string;
  colors: string[];
  isSplit: boolean;
  lineType: string;
  tooltip?: string;
  testId?: string;
  swatchTestId?: string;
};

export function nextForecastLegendVisibility(
  visibleLines: Set<string>,
  lineType: string,
): Set<string> | null {
  const isVisible = visibleLines.has(lineType);
  if (isVisible && visibleLines.size === 1) {
    return null;
  }
  const next = new Set(visibleLines);
  if (isVisible) {
    next.delete(lineType);
  } else {
    next.add(lineType);
  }
  return next;
}

export function forecastLegendEntryView(
  entry: ForecastLegendEntry,
  visibleLines: Set<string>,
  canToggle: boolean,
): {
  pastColor: string;
  futureColor: string;
  isVisible: boolean;
  isDisabled: boolean;
  testId: string | undefined;
  swatchTestId: string | undefined;
  wrapTooltip: boolean;
} {
  const isVisible = visibleLines.has(entry.lineType);
  const isReported = entry.lineType === FORECAST_LINE_VISIBILITY.reported;
  const pastColor = entry.colors[0] ?? colors.black.main;
  return {
    pastColor,
    futureColor: entry.colors[1] ?? pastColor,
    isVisible,
    isDisabled: !canToggle || (isVisible && visibleLines.size === 1),
    testId:
      entry.testId ?? (isReported ? 'forecast-reported-legend' : undefined),
    swatchTestId:
      entry.swatchTestId ??
      (isReported ? 'forecast-reported-legend-swatch' : undefined),
    wrapTooltip: Boolean(entry.tooltip),
  };
}

function ForecastLegendItem({
  entry,
  visibleLines,
  onToggle,
  canToggle,
}: {
  entry: ForecastLegendEntry;
  visibleLines: Set<string>;
  onToggle: (lineType: string) => void;
  canToggle: boolean;
}) {
  const view = forecastLegendEntryView(entry, visibleLines, canToggle);
  const button = (
    <ForecastReportedLegendEntry
      label={entry.label}
      pastColor={view.pastColor}
      futureColor={view.futureColor}
      isSplit={entry.isSplit}
      isVisible={view.isVisible}
      isDisabled={view.isDisabled}
      onClick={() => onToggle(entry.lineType)}
      testId={view.testId}
      swatchTestId={view.swatchTestId}
      title={view.wrapTooltip ? undefined : entry.tooltip}
    />
  );
  if (!view.wrapTooltip) {
    return <Fragment>{button}</Fragment>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span style={{ display: 'inline-flex' }}>{button}</span>
      </TooltipTrigger>
      <TooltipContent>{entry.tooltip}</TooltipContent>
    </Tooltip>
  );
}

export function ForecastLegend({
  entries,
  visibleLines,
  onToggle,
  canToggle,
  testId,
}: {
  entries: ForecastLegendEntry[];
  visibleLines: Set<string>;
  onToggle: (lineType: string) => void;
  canToggle: boolean;
  testId?: string;
}) {
  if (entries.length === 0) {
    return null;
  }

  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '40px',
        paddingTop: '20px',
      }}
    >
      {entries.map((entry, index) => (
        <ForecastLegendItem
          key={index}
          entry={entry}
          visibleLines={visibleLines}
          onToggle={onToggle}
          canToggle={canToggle}
        />
      ))}
    </div>
  );
}
